import { CalendarToday } from "@mui/icons-material";
import { Box, Button, Popover, Tooltip } from "@mui/material";
import {
	addDays,
	endOfMonth,
	endOfYear,
	format,
	isSameDay,
	startOfDay,
	startOfMonth,
	startOfYear,
	subDays,
	subMonths,
	subYears,
} from "date-fns";
import { enUS } from "date-fns/locale";
import { useState } from "react";
import { DateRangePicker } from "react-date-range";
const defaultStaticRanges = [
	{
		label: "Today",
		range: () => {
			const today = startOfDay(new Date());
			return { startDate: today, endDate: today };
		},
		isSelected: (range) => {
			const today = startOfDay(new Date()).getTime();
			return range.startDate.getTime() === today && range.endDate.getTime() === today;
		},
	},
	{
		label: "Yesterday",
		range: () => {
			const yesterday = startOfDay(addDays(new Date(), -1));
			return { startDate: yesterday, endDate: yesterday };
		},
		isSelected: (range) => {
			const yesterday = startOfDay(addDays(new Date(), -1)).getTime();
			return range.startDate.getTime() === yesterday && range.endDate.getTime() === yesterday;
		},
	},
	{
		label: "Last 7 Days",
		range: () => {
			const end = startOfDay(new Date());
			const start = startOfDay(subDays(end, 6));
			return { startDate: start, endDate: end };
		},
		isSelected: (range) => {
			const end = startOfDay(new Date()).getTime();
			const start = startOfDay(subDays(new Date(), 6)).getTime();
			return range.startDate.getTime() === start && range.endDate.getTime() === end;
		},
	},
	{
		label: "Last 30 Days",
		range: () => {
			const end = startOfDay(new Date());
			const start = startOfDay(subDays(end, 29));
			return { startDate: start, endDate: end };
		},
		isSelected: (range) => {
			const end = startOfDay(new Date()).getTime();
			const start = startOfDay(subDays(new Date(), 29)).getTime();
			return range.startDate.getTime() === start && range.endDate.getTime() === end;
		},
	},
	{
		label: "Last 90 Days",
		range: () => {
			const end = startOfDay(new Date());
			const start = startOfDay(subDays(end, 89));
			return { startDate: start, endDate: end };
		},
		isSelected: (range) => {
			const end = startOfDay(new Date()).getTime();
			const start = startOfDay(subDays(new Date(), 89)).getTime();
			return range.startDate.getTime() === start && range.endDate.getTime() === end;
		},
	},
	{
		label: "Last Month",
		range: () => {
			const start = startOfMonth(subMonths(new Date(), 1));
			const end = endOfMonth(subMonths(new Date(), 1));
			return { startDate: start, endDate: end };
		},
		isSelected: (range) => {
			const start = startOfMonth(subMonths(new Date(), 1)).getTime();
			const end = endOfMonth(subMonths(new Date(), 1)).getTime();
			return range.startDate.getTime() === start && range.endDate.getTime() === end;
		},
	},
	{
		label: "Last 12 Months",
		range: () => {
			const end = startOfDay(new Date());
			const start = startOfDay(subDays(end, 364));
			return { startDate: start, endDate: end };
		},
		isSelected: (range) => {
			const end = startOfDay(new Date()).getTime();
			const start = startOfDay(subDays(new Date(), 364)).getTime();
			return range.startDate.getTime() === start && range.endDate.getTime() === end;
		},
	},
	{
		label: "Last Year",
		range: () => {
			const start = startOfYear(subYears(new Date(), 1));
			const end = endOfYear(subYears(new Date(), 1));
			return { startDate: start, endDate: end };
		},
		isSelected: (range) => {
			const start = startOfYear(subYears(new Date(), 1)).getTime();
			const end = endOfYear(subYears(new Date(), 1)).getTime();
			return range.startDate.getTime() === start && range.endDate.getTime() === end;
		},
	},
];

const CustomDateRangePicker = ({ dateRange, setDateRange, defaultDateRange, staticRanges = defaultStaticRanges }) => {
	const [selectedDate, setSelectedDate] = useState([
		{
			startDate: new Date(),
			endDate: new Date(),
			key: "selection",
		},
	]);
	const [selectedRange, setSelectedRange] = useState([defaultDateRange]);
	const [selectedLabel, setSelectedLabel] = useState(staticRanges[0].label);

	const [anchorEl, setAnchorEl] = useState(null);

	const open = Boolean(anchorEl);
	const id = open ? "simple-popover" : undefined;
	const range = selectedRange[0];
	const buttonLabel = selectedLabel
		? selectedLabel
		: `${format(range.startDate, "MMM d, yyyy")} - ${format(range.endDate, "MMM d, yyyy")}`;
	const handleDateRangeChange = (item) => {
		setSelectedDate([item.selection]);
		setDateRange(item.selection);
	};
	const handleClose = () => {
		setAnchorEl(null);
	};
	const handleCancel = () => {
		setSelectedDate(selectedRange);
		setAnchorEl(null);
	};
	const handleClick = (event) => {
		setAnchorEl(event.currentTarget);
	};
	const matchStaticRange = (startDate, endDate) => {
		for (const rangeObj of staticRanges) {
			const { startDate: staticStart, endDate: staticEnd } = rangeObj.range();
			if (isSameDay(staticStart, startDate) && isSameDay(staticEnd, endDate)) {
				return rangeObj.label;
			}
		}
		return null;
	};

	const handleRangeChange = (item) => {
		// const { startDate, endDate } = item.selection;
		// console.log("selectedDate", selectedDate);
		// setFilterData(defaultShopifyLiveData);
		const startDate = selectedDate[0]?.startDate;
		const endDate = selectedDate[0]?.endDate;
		setSelectedRange(selectedDate);

		const matchedLabel = matchStaticRange(startDate, endDate);
		if (matchedLabel) {
			setSelectedLabel(matchedLabel);
		} else {
			setSelectedLabel(null);
		}
		handleClose();
	};

	return (
		<Box display="flex" justifyContent="flex-end" flexDirection="column">
			<Button
				aria-describedby={id}
				variant="outlined"
				onClick={handleClick}
				color="secondary"
				sx={{ borderRadius: "4px", width: "100%", fontWeight: 400, textTransform: "capitalize" }}>
				<CalendarToday sx={{ height: "20px", width: "20px", marginRight: "5px", flexShrink: 0 }} />
				<Tooltip title={buttonLabel} arrow>
					<Box
						component="span"
						sx={{
							textOverflow: "ellipsis",
							overflow: "hidden",
							whiteSpace: "nowrap",
							display: "inline-block",
							maxWidth: "90%",
						}}>
						{buttonLabel}
					</Box>
				</Tooltip>
			</Button>
			<Popover
				id={id}
				open={open}
				anchorEl={anchorEl}
				// onClose={handleClose}
				anchorOrigin={{
					vertical: "bottom",
					horizontal: "left",
				}}>
				<Box>
					<DateRangePicker
						onChange={handleDateRangeChange}
						showSelectionPreview={true}
						moveRangeOnFirstSelection={false}
						months={2}
						ranges={selectedDate}
						direction="horizontal"
						locale={enUS}
						staticRanges={staticRanges}
						inputRanges={[]}
						maxDate={new Date()}
						// style={{ width: 200, height: 100 }}
					/>
					<Box display="flex" justifyContent="flex-end" padding="0px 10px 10px 10px" height="25px">
						<Button
							variant="contained"
							sx={{ margin: "0px 5px", fontSize: "13px", textTransform: "capitalize" }}
							onClick={handleCancel}>
							Cancel
						</Button>
						<Button
							variant="contained"
							sx={{ margin: "0px 5px", fontSize: "13px", textTransform: "capitalize" }}
							onClick={handleRangeChange}>
							Apply
						</Button>
					</Box>
				</Box>
			</Popover>
		</Box>
	);
};

export default CustomDateRangePicker;
